import { useCallback, useEffect, useMemo } from 'react';

import { usePosDataQuery } from 'generated/sanity-graphql';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { getRestaurantsByStoreNumber } from 'remote/api/restaurants';
import { useLocationContext } from 'state/location';
import { useLoggerContext } from 'state/logger';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useNetworkContext } from 'state/network';
import { useServiceModeContext } from 'state/service-mode';
import { isKiosk } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { transformPlus } from 'utils/menu';
import { parseStringifiedJSON } from 'utils/parse-string';
import { mergeRestaurantData, useGetRestaurantFn } from 'utils/restaurant';
import { routes } from 'utils/routing';

import getRestaurantPosDataPlus from './get-restaurant-pos-data-plus';
import { IStoreContext, StoreProxy } from './types';
import useRefreshStore from './use-refresh-store';

export const resetLastTimeStoreUpdated: IStoreContext['resetLastTimeStoreUpdated'] = currentTime =>
  LocalStorage.setItem(StorageKeys.LAST_TIME_STORE_UPDATED, currentTime || Date.now());

export const useStoreUtils = ({
  resetStore,
  store,
}: {
  resetStore: IStoreContext['resetStore'];
  store: StoreProxy;
}) => {
  const { location, navigate } = useLocationContext();
  const { decorateLogger, logger } = useLoggerContext();
  const mParticle = useMParticleContext();
  const { connection, sanityEndpoints } = useNetworkContext();
  const { serviceMode } = useServiceModeContext();
  const getRestaurant = useGetRestaurantFn();

  const menuPullLogAttrs: { StoreId?: string } = {};
  let storePosDataId: string | null | undefined;
  let storeEmail = '';

  menuPullLogAttrs.StoreId = store.number ?? undefined;
  storePosDataId = store.restaurantPosData?._id;
  storeEmail = store.email?.[0] ?? '';

  const { serviceModeStatus, isRestaurantAvailable } = useServiceModeStatus(store);

  const { data: posData, refetch: getPosData } = usePosDataQuery({
    fetchPolicy: isKiosk ? 'network-only' : 'cache-and-network',
    onError: () => {
      if (isKiosk) {
        mParticle.logEvent(
          CustomEventNames.KIOSK_MENU_PULL_ERROR,
          EventTypes.Other,
          menuPullLogAttrs
        );
      }
    },
    onCompleted: () => {
      if (isKiosk) {
        mParticle.logEvent(
          CustomEventNames.KIOSK_MENU_PULL_SUCCESS,
          EventTypes.Other,
          menuPullLogAttrs
        );
      }
    },
    skip: !storePosDataId,
    variables: {
      id: storePosDataId || '',
    },
  });

  const disableChickenSandwich = useMemo(
    () => Boolean(posData?.RestaurantPosData?.disableChickenSandwich),
    [posData]
  );

  const onConfirmLocateRestaurants = useCallback(() => {
    resetStore();

    if (location.pathname !== routes.storeLocator) {
      navigate(routes.storeLocator);
    }
  }, [location.pathname, navigate, resetStore]);

  const fetchStore = useCallback(
    async (storeId: string) => {
      const rbiRestaurant = await getRestaurant(storeId);

      const isRestaurantPosAvailable = rbiRestaurant.available;

      if (!isKiosk && !isRestaurantPosAvailable) {
        onConfirmLocateRestaurants();
        return;
      }

      const [sanityStore] = await getRestaurantsByStoreNumber(sanityEndpoints.groq, {
        connection,
        storeNumber: storeId,
      });

      return mergeRestaurantData({ rbiRestaurant, sanityStore });
    },
    [connection, onConfirmLocateRestaurants, sanityEndpoints.groq] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const prices: Record<string, number> | null = useMemo(() => {
    const rawPlus = getRestaurantPosDataPlus(posData, serviceMode);

    if (!rawPlus) {
      return null;
    }

    return transformPlus(parseStringifiedJSON({ defaultValue: {}, value: rawPlus }));
  }, [posData, serviceMode]);

  useEffect(() => {
    const storeId = store.number ?? 'No store selected';

    decorateLogger({
      storeId,
    });

    if (!store._id) {
      return;
    }

    if (!store.restaurantPosData) {
      return logger.error({ message: 'No POS data for selected store', storeId: store._id });
    }
  }, [store]); // eslint-disable-line react-hooks/exhaustive-deps

  useRefreshStore({
    getRestaurantPosData: (storeId: string) => {
      getPosData({
        id: storeId,
      });
    },
    store,
  });

  return {
    disableChickenSandwich,
    email: storeEmail,
    fetchStore,
    isRestaurantAvailable,
    prices,
    resetLastTimeStoreUpdated,
    serviceModeStatus,
  };
};
